import { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Loader2, Pencil, RotateCcw, Search, XCircle } from "lucide-react";
import type { ComplaintRecord, ComplaintStatus, MasterLookupRecord } from "@/types";

const NEXT: Record<ComplaintStatus, ComplaintStatus[]> = {
  OPEN: ["INVESTIGATING", "RESOLVED", "REJECTED"],
  INVESTIGATING: ["RESOLVED", "REJECTED"],
  RESOLVED: ["OPEN"],
  REJECTED: ["OPEN"],
};
const BUTTON: Record<ComplaintStatus, { label: string; icon: typeof Check; variant: "default" | "outline" }> = {
  OPEN: { label: "Reopen", icon: RotateCcw, variant: "outline" },
  INVESTIGATING: { label: "Start investigating", icon: Search, variant: "outline" },
  RESOLVED: { label: "Resolve", icon: Check, variant: "default" },
  REJECTED: { label: "Reject", icon: XCircle, variant: "outline" },
};
const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

interface Props {
  complaint: ComplaintRecord;
  onChanged: () => void;
  onEdit: (c: ComplaintRecord) => void;
  compact?: boolean;
}

/** The next steps of a complaint: investigate, resolve (how, and what was given), reject or reopen, each with what it needs */
export function ComplaintActions({ complaint, onChanged, onEdit, compact }: Props) {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const canUpdate = hasPermission("complaint.update");
  const [target, setTarget] = useState<ComplaintStatus | null>(null);
  const [notes, setNotes] = useState("");
  const [resolution, setResolution] = useState("");
  const [amount, setAmount] = useState("");
  const [resolutions, setResolutions] = useState<MasterLookupRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const size = compact ? "h-7 text-[11px] px-2" : "h-8 text-xs";

  useEffect(() => {
    if (target !== "RESOLVED" || resolutions.length) return;
    api.get("/api/MasterLookup/ByCategory/RESOLUTION_TYPE").then((r) => setResolutions(unwrapArray<MasterLookupRecord>(r))).catch(() => undefined);
  }, [target]);

  if (!canUpdate) return null;
  const isOpen = complaint.complaint_status === "OPEN" || complaint.complaint_status === "INVESTIGATING";

  const send = async (st: ComplaintStatus, extra: Record<string, any> = {}) => {
    setBusy(true);
    try {
      await api.patch(`/api/Complaint/ChangeStatus/${complaint.id}`, { status: st, ...extra });
      success("Complaint updated", "");
      setTarget(null);
      onChanged();
    } catch (err: any) {
      error("Could not update", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  const open = (st: ComplaintStatus) => {
    if (st === "INVESTIGATING") return send(st);
    setNotes("");
    setResolution("");
    setAmount(complaint.claim_amount != null ? "" : "");
    setTarget(st);
  };

  const submit = () => {
    if (!target) return;
    send(target, {
      notes: notes.trim() || undefined,
      resolution_type: target === "RESOLVED" ? resolution : undefined,
      resolved_amount: target === "RESOLVED" && amount !== "" ? Number(amount) : undefined,
    });
  };

  const title = target === "RESOLVED" ? "Resolve complaint" : target === "REJECTED" ? "Reject complaint" : "Reopen complaint";
  const canSubmit = !!target && notes.trim() && (target !== "RESOLVED" || resolution);

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {NEXT[complaint.complaint_status].map((st) => {
          const b = BUTTON[st];
          const Icon = b.icon;
          return (
            <Button key={st} size="sm" variant={b.variant} className={`${size} gap-1`} disabled={busy} onClick={() => open(st)}>
              <Icon className="size-3.5" /> {b.label}
            </Button>
          );
        })}
        {isOpen && (
          <Button size="sm" variant="ghost" className={`${size} gap-1`} onClick={() => onEdit(complaint)} title="Edit details">
            <Pencil className="size-3.5" />
          </Button>
        )}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && !busy && setTarget(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {complaint.complaint_number}
              {complaint.claim_amount ? ` · claim ₹${Number(complaint.claim_amount).toLocaleString("en-IN")}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {target === "RESOLVED" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>How was it settled? *</Label>
                  <select value={resolution} onChange={(e) => setResolution(e.target.value)} className={field}>
                    <option value="">-- Choose --</option>
                    {resolutions.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Credit / discount given (₹)</Label>
                  <Input type="number" min={0} step="any" value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{target === "RESOLVED" ? "What was done *" : target === "REJECTED" ? "Why is it rejected? *" : "Why is it being reopened? *"}</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)} disabled={busy}>
              Back
            </Button>
            <Button variant={target === "REJECTED" ? "destructive" : "default"} onClick={submit} disabled={busy || !canSubmit}>
              {busy ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
